import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useOrg, useScheduledSeats } from "@/hooks";
import { cycleNoun } from "@/lib/money";
import { ApiError } from "@/services";
import { useState } from "react";

/**
 * Change the seat count for the next renewal.
 *
 * SCHEDULED, NOT IMMEDIATE, and the card says why. The current period is paid
 * for, so shrinking the pool today would take away capacity the org has already
 * bought. Removing a PERSON frees their slot right now — that is a different
 * action, on the Seats screen, and conflating the two is how an admin ends up
 * believing they have cancelled something they have not.
 */
export function ScheduledSeatsCard() {
	const org = useOrg();
	const schedule = useScheduledSeats();
	const [value, setValue] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	const seats = org.data?.seats;
	const scheduled = org.data?.org.seats_scheduled ?? null;
	if (!seats || seats.purchased == null) return null;

	const renewal = org.data?.org.contract_ends_at
		? new Date(org.data.org.contract_ends_at).toLocaleDateString()
		: `your next ${cycleNoun(org.data?.plan?.billing_cycle)}`;

	async function save(next: number | null) {
		setError(null);
		setSaved(false);
		try {
			await schedule.mutateAsync(next);
			setValue("");
			setSaved(true);
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Couldn't save that. Try again.",
			);
		}
	}

	const parsed = Number.parseInt(value, 10);
	const valid = Number.isInteger(parsed) && parsed >= 0;

	return (
		<div className="border-t border-divider pt-4">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<div className="text-body font-medium text-heading">
						Seats at renewal
					</div>
					<p className="mt-0.5 max-w-md text-caption text-muted-foreground">
						{scheduled == null
							? `You'll renew with ${seats.purchased} seats. Change it here — this period is already paid for, so a reduction applies from ${renewal}.`
							: `Scheduled to change from ${seats.purchased} to ${scheduled} seats on ${renewal}.`}
					</p>
				</div>

				<div className="flex items-center gap-2">
					<Input
						type="number"
						min={0}
						className="w-24 tabular"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder={String(scheduled ?? seats.purchased)}
					/>
					<Button
						variant="secondary"
						size="sm"
						disabled={!valid || schedule.isPending}
						onClick={() => save(parsed)}
					>
						{schedule.isPending ? "Saving…" : "Schedule"}
					</Button>
					{/*
					 * Only offered when something IS scheduled. A "cancel" that
					 * does nothing is a button that teaches people the UI lies.
					 */}
					{scheduled != null ? (
						<Button
							variant="ghost"
							size="sm"
							disabled={schedule.isPending}
							onClick={() => save(null)}
						>
							Cancel change
						</Button>
					) : null}
				</div>
			</div>

			{error ? <p className="mt-2 text-caption text-error">{error}</p> : null}
			{saved && !error ? (
				<p className="mt-2 text-caption text-success">Saved.</p>
			) : null}
		</div>
	);
}
